// Device-level settings (16.69) -- deliberately NOT part of GameSaveData.
// These are per-device/per-browser preferences (how loud, what's drawn),
// not captain progress, so they must survive New Game, must never be
// wiped by loading someone else's save, and must NOT travel between
// devices through the cloud save the way real progress does. Separate
// localStorage key from `starforge_save`, same best-effort
// no-op-on-failure convention as localSave.ts (storage full or private
// browsing silently degrades to in-memory defaults rather than throwing).

const SETTINGS_KEY = "starforge_settings";

export type ZoomLevel = "close" | "medium" | "far";

export interface Settings {
  // Audio (16.69). Volumes are 0-1 linear scalars; the audio layer itself
  // applies them multiplicatively (master x sfx), so muting master kills
  // everything without losing the individual levels.
  masterVolume: number;
  sfxVolume: number;
  muted: boolean;
  // Graphics: the one genuinely adjustable thing the renderer has --
  // 16.59's FxEffect layer (beams/projectiles/particles/pulses) is
  // purely cosmetic and spawned alongside the real damage logic, so
  // switching it off changes nothing mechanical.
  showCombatFx: boolean;
  // Which of Section 4b's three locked ZOOM_RADII the Expedition View
  // opens at (expeditionScreen.ts's own `ZOOM_RADII`, 16.57).
  defaultZoom: ZoomLevel;
  // Was session-only before 16.69 (state.devDebugOn's "resets off on
  // reload"); now persisted, since a settings screen that forgets its
  // own toggle is worse than the reset-on-reload safety it bought.
  devDebugOn: boolean;
}

export const DEFAULT_SETTINGS: Settings = {
  masterVolume: 0.7,
  sfxVolume: 0.8,
  muted: false,
  showCombatFx: true,
  defaultZoom: "medium",
  devDebugOn: false
};

function clamp01(value: unknown, fallback: number): number {
  return typeof value === "number" && Number.isFinite(value)
    ? Math.min(1, Math.max(0, value))
    : fallback;
}

// Field-by-field coercion rather than a blind spread: a hand-edited or
// half-written settings blob should degrade to defaults per-field, never
// poison the live object with a NaN volume or a bogus zoom level.
export function normalizeSettings(raw: unknown): Settings {
  const r = (raw ?? {}) as Partial<Settings>;
  const zoom: ZoomLevel[] = ["close", "medium", "far"];
  return {
    masterVolume: clamp01(r.masterVolume, DEFAULT_SETTINGS.masterVolume),
    sfxVolume: clamp01(r.sfxVolume, DEFAULT_SETTINGS.sfxVolume),
    muted: typeof r.muted === "boolean" ? r.muted : DEFAULT_SETTINGS.muted,
    showCombatFx:
      typeof r.showCombatFx === "boolean" ? r.showCombatFx : DEFAULT_SETTINGS.showCombatFx,
    defaultZoom:
      typeof r.defaultZoom === "string" && zoom.includes(r.defaultZoom as ZoomLevel)
        ? (r.defaultZoom as ZoomLevel)
        : DEFAULT_SETTINGS.defaultZoom,
    devDebugOn: typeof r.devDebugOn === "boolean" ? r.devDebugOn : DEFAULT_SETTINGS.devDebugOn
  };
}

export function readSettings(): Settings {
  try {
    const raw = localStorage.getItem(SETTINGS_KEY);
    if (!raw) return { ...DEFAULT_SETTINGS };
    return normalizeSettings(JSON.parse(raw));
  } catch {
    return { ...DEFAULT_SETTINGS };
  }
}

export function writeSettings(value: Settings): void {
  try {
    localStorage.setItem(SETTINGS_KEY, JSON.stringify(value));
  } catch {
    // Best-effort, same as localSave.ts -- an unwritable store still
    // leaves `settings` correct for this session.
  }
}

// Live mutable singleton, same pattern devTuning.ts already uses: every
// consumer reads fresh off this object, so a settings change takes effect
// immediately with no re-wiring or event bus. Call saveSettings() after
// mutating to make it stick.
export const settings: Settings = readSettings();

export function saveSettings(): void {
  writeSettings(settings);
}

export function updateSettings(patch: Partial<Settings>): void {
  Object.assign(settings, normalizeSettings({ ...settings, ...patch }));
  saveSettings();
}

export function resetSettings(): void {
  Object.assign(settings, DEFAULT_SETTINGS);
  saveSettings();
}

// Effective 0-1 gain for a sound effect, folding master/sfx/mute together
// -- pure so the volume math is testable without a real AudioContext.
export function effectiveSfxGain(s: Settings = settings): number {
  return s.muted ? 0 : s.masterVolume * s.sfxVolume;
}
